import * as Sentry from '@sentry/nextjs';
import type { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import {
  address,
  createSolanaRpc,
  getCompiledTransactionMessageDecoder,
  getCompiledTransactionMessageEncoder,
  getTransactionDecoder,
  getTransactionEncoder,
  signatureBytes,
  type Base64EncodedWireTransaction,
  type TransactionMessageBytes,
  type TransactionVersion,
} from '@solana/kit';
import { Decimal } from 'decimal.js-light';

import { logger } from '@/lib/logger';

import { CHAIN_IDS } from '@/constants/chain';
import { JWT_PROTECTED_JSON_RPC_HTTP_URL_BY_CHAIN_ID } from '@/constants/rpc';

import { getNodeProviderJwt } from '@/hooks/useNodeProviderToken';

import type { SolanaTransactionVersion } from './0x/solana/assembly';
import type { SolanaHash } from './models';
import { createJwtAuthenticatedSolanaRpc } from './solanaRpcTransport';

let sharedRpc: ReturnType<typeof createJwtAuthenticatedSolanaRpc> | undefined;

/**
 * Client-side Solana kit RPC. Auth uses the rotating node-provider JWT from local storage
 * (or an explicit `jwt` override), minted per request inside the transport so the cached
 * client never sends a stale token. Commitment is passed per call by consumers.
 */
export function getSolanaRpc(jwt?: string) {
  if (jwt) {
    return createJwtAuthenticatedSolanaRpc(
      JWT_PROTECTED_JSON_RPC_HTTP_URL_BY_CHAIN_ID[CHAIN_IDS.SOLANA],
      () => jwt,
    );
  }
  sharedRpc ??= createJwtAuthenticatedSolanaRpc(
    JWT_PROTECTED_JSON_RPC_HTTP_URL_BY_CHAIN_ID[CHAIN_IDS.SOLANA],
    getNodeProviderJwt,
  );
  return sharedRpc;
}

let sharedJitoRpc: ReturnType<typeof createSolanaRpc> | undefined;

/**
 * Kit RPC for the Jito block engine's transactions endpoint. Submit-only and unauthenticated
 * (unlike {@link getSolanaRpc}); used exclusively by {@link fillSolanaOrder}.
 */
function getSolanaJitoRpc() {
  sharedJitoRpc ??= createSolanaRpc('https://mainnet.block-engine.jito.wtf/api/v1/transactions');
  return sharedJitoRpc;
}

type FillSolanaOrderArgs = {
  transaction: string | Uint8Array;
  walletClient: ConnectedStandardSolanaWallet;
  signTransaction: {
    (input: {
      wallet: ConnectedStandardSolanaWallet;
      transaction: Uint8Array;
      chain?: string;
    }): Promise<{ signedTransaction: Uint8Array }>;
    (
      ...inputs: {
        wallet: ConnectedStandardSolanaWallet;
        transaction: Uint8Array;
        chain?: string;
      }[]
    ): Promise<{ signedTransaction: Uint8Array }[]>;
  };
};

/**
 * Read from the Wallet Standard `solana:signTransaction` feature. v0 is the
 * floor because every wallet supports it.
 */
export const getSolanaWalletMaxTransactionVersion = (
  wallet: Pick<ConnectedStandardSolanaWallet, 'standardWallet'> | undefined,
): SolanaTransactionVersion => {
  // `supportedTransactionVersions` is typed 'legacy' | 0 upstream; wallets
  // that have shipped v1 report the number 1 at runtime.
  const supported = wallet?.standardWallet?.features?.['solana:signTransaction'] as
    | { supportedTransactionVersions?: readonly (string | number)[] }
    | undefined;
  return supported?.supportedTransactionVersions?.includes(1) ? 1 : 0;
};

const readTransactionVersion = (
  serializedTransaction: Uint8Array,
): TransactionVersion | undefined => {
  try {
    const tx = getTransactionDecoder().decode(serializedTransaction);
    return getCompiledTransactionMessageDecoder().decode(tx.messageBytes).version;
  } catch {
    return undefined;
  }
};

/**
 * Some WalletConnect wallets return a bare Ed25519 signature from Privy's
 * signTransaction hook. Attach it to the original transaction before sending.
 */
export const normalizeSignedSolanaTransaction = ({
  transaction,
  signedTransaction,
  signerAddress,
}: {
  transaction: Uint8Array;
  signedTransaction: Uint8Array;
  signerAddress: string;
}): Uint8Array => {
  if (signedTransaction.length !== 64) {
    return signedTransaction;
  }

  const decodedTransaction = getTransactionDecoder().decode(transaction);
  return new Uint8Array(
    getTransactionEncoder().encode({
      ...decodedTransaction,
      signatures: {
        ...decodedTransaction.signatures,
        [address(signerAddress)]: signatureBytes(signedTransaction),
      },
    }),
  );
};

/**
 * Swaps the blockhash of an unsigned transaction for a fresh one, operating
 * on the compiled message (no address-lookup-table resolution needed) and
 * preserving the zeroed signature slots so the wallet still signs it.
 *
 * Quotes are built server-side at quote time, so a quote that sat past the
 * ~60-90s blockhash TTL carries an expired blockhash. The wallet can no longer
 * simulate such a transaction, which surfaces as a missing balance-change
 * preview plus a "could be malicious" warning (and an eventual failed send).
 * Best-effort: returns the input unchanged on any failure so a hiccup here
 * cannot block a send that might otherwise succeed.
 */
const withFreshBlockhash = async (serializedTransaction: Uint8Array): Promise<Uint8Array> => {
  try {
    const tx = getTransactionDecoder().decode(serializedTransaction);
    const message = getCompiledTransactionMessageDecoder().decode(tx.messageBytes);
    const {
      value: { blockhash },
    } = await getSolanaRpc().getLatestBlockhash({ commitment: 'finalized' }).send();
    const messageBytes = getCompiledTransactionMessageEncoder().encode({
      ...message,
      lifetimeToken: blockhash as typeof message.lifetimeToken,
    }) as unknown as TransactionMessageBytes;
    return new Uint8Array(getTransactionEncoder().encode({ ...tx, messageBytes }));
  } catch (error) {
    logger.warn('Failed to refresh Solana transaction blockhash; submitting original transaction', {
      error,
    });
    return serializedTransaction;
  }
};

/**
 * Signs and submits a serialized Solana order through Jito.
 */
export const fillSolanaOrder = async ({
  transaction,
  walletClient,
  signTransaction,
}: FillSolanaOrderArgs): Promise<SolanaHash> => {
  return Sentry.startSpan(
    {
      name: 'fillSolanaOrder',
      op: 'submitTransaction',
    },
    async (span) => {
      span.setAttribute('user.account.address', walletClient.address);
      span.setAttribute('user.account.chainID', CHAIN_IDS.SOLANA);

      const swapTxBuffer =
        typeof transaction === 'string'
          ? new Uint8Array(Buffer.from(transaction, 'base64'))
          : transaction;

      // The connected wallet can have changed since the quote was built. Failing
      // here invalidates the quote rather than letting the wallet mis-simulate.
      if (
        getSolanaWalletMaxTransactionVersion(walletClient) !== 1 &&
        readTransactionVersion(swapTxBuffer) === 1
      ) {
        throw new Error(
          'Unsupported transaction type: connected Solana wallet cannot sign version 1',
        );
      }

      const transactionToSign = await withFreshBlockhash(swapTxBuffer);
      const { signedTransaction } = await signTransaction({
        wallet: walletClient,
        transaction: transactionToSign,
        chain: 'solana:mainnet',
      });

      const normalizedSignedTransaction = normalizeSignedSolanaTransaction({
        transaction: transactionToSign,
        signedTransaction,
        signerAddress: walletClient.address,
      });

      const base64SignedTransaction = Buffer.from(normalizedSignedTransaction).toString(
        'base64',
      ) as Base64EncodedWireTransaction;

      // web3.js sent `[tx, { encoding: 'base64' }]`, leaving preflight to the server defaults
      // (skipPreflight=false, preflightCommitment=finalized). The explicit 'finalized' stops kit
      // from injecting its own 'confirmed' default, so the wire request stays identical.
      const txHash = await getSolanaJitoRpc()
        .sendTransaction(base64SignedTransaction, {
          encoding: 'base64',
          preflightCommitment: 'finalized',
        })
        .send();

      return txHash;
    },
  );
};

const LAMPORTS_PER_SOL = 1_000_000_000;

export function lamportToSol(lamport: number | string): string {
  return new Decimal(lamport).div(LAMPORTS_PER_SOL).toString();
}
